import { Temporal } from "@js-temporal/polyfill";
import { dateStr, instantDateTime, timeStr } from "../schemas/common";
import { accessChoice, localPoint, reservation, scheduleTimezone, scheduleV1, type AccessChoice, type LocalPoint, type ScheduleV1 } from "../schemas/schedule";
import { evaluation, type EntityRef, type ValidationEvaluation, type ValidationIssue } from "./issues";

export interface ResolvedPoint {
  date: string | null; time: string | null; offset: string | null;
  /** Original local precision is preserved. UTC projections use ISO milliseconds. */
  local: string | null; utc: string | null;
}
export interface ScheduleProjections {
  eventDate: string | null; eventEndDate: string | null; endDate: string | null;
  setupStartsAt: string | null; startsAt: string | null; endsAt: string | null; vacateAt: string | null;
  accessStartUtc: string | null; accessEndUtc: string | null;
  calendarFrom: string | null; calendarThrough: string | null;
}
export interface ScheduleResolution extends ValidationEvaluation {
  schedule: ScheduleV1 | null;
  points: Record<"accessStart" | "eventStart" | "eventEnd" | "accessEnd", ResolvedPoint>;
  durations: Record<"setupMinutes" | "eventMinutes" | "cleanupMinutes" | "accessMinutes", number | null>;
  projections: ScheduleProjections;
  /** A legacy unknown bound is never equivalent to an unoccupied venue. */
  reservationState: "timed" | "all_day" | "legacy_unresolved" | "incomplete" | "invalid";
  legacyBounds: { startDate: string | null; endDate: string | null; unknownBounds: boolean } | null;
}
const emptyPoint = (): ResolvedPoint => ({ date: null, time: null, offset: null, local: null, utc: null });
const asObject = (value: unknown): Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value) ? value as Record<string, unknown> : {};
const utcString = (value: Temporal.Instant) => {
  const result = value.toString({ smallestUnit: "millisecond" });
  // Indexed ISO text comparisons rely on one fixed-width year representation.
  if (!instantDateTime.safeParse(result).success) throw new RangeError("Outside supported UTC calendar range");
  return result;
};
const validDate = (value: unknown): string | null => dateStr.safeParse(value).success ? value as string : null;
const validTime = (value: unknown): string | null => timeStr.safeParse(value).success ? value as string : null;
export function parseScheduleJson(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try { return JSON.parse(value); } catch { return null; }
}

/** Standalone local-point resolution for tours and other captured-zone users. */
export function resolveLocalPoint(value: unknown, timezone: unknown, options: { entity?: EntityRef; field?: string } = {}): ValidationEvaluation & { point: ResolvedPoint } {
  const entity = options.entity ?? { type: "booking" }, field = options.field ?? "point", issues: ValidationIssue[] = [];
  const add = (code: string, suffix: string, message: string) => issues.push({ code: `${entity.type}.${code}`, kind: "error", entity, fields: [suffix ? `${field}.${suffix}` : field], message, blocks: [`${entity.type}.save`, ...(entity.type === "booking" ? ["booking.reserve", "contract.send"] as const : entity.type === "tour" ? ["tour.schedule"] as const : [])] });
  const shape = localPoint.safeParse(value), zone = scheduleTimezone.safeParse(timezone);
  if (!shape.success) for (const error of shape.error.issues) add("point_invalid", error.path.map(String).join("."), error.message);
  if (!zone.success) add("timezone_invalid", "timezone", "Choose a valid IANA timezone.");
  const raw = asObject(value), date = validDate(raw.date), time = validTime(raw.time);
  const point: ResolvedPoint = { date, time, offset: null, local: date && time ? `${date}T${time}` : null, utc: null };
  if (shape.success && zone.success && point.local) try {
    const plain = Temporal.PlainDateTime.from(point.local);
    const earlier = plain.toZonedDateTime(zone.data, { disambiguation: "earlier" });
    const later = plain.toZonedDateTime(zone.data, { disambiguation: "later" });
    if (!earlier.toPlainDateTime().equals(plain) || !later.toPlainDateTime().equals(plain)) {
      add("time_nonexistent", "time", "This clock time does not exist in this timezone. Choose another time.");
    } else if (earlier.epochNanoseconds !== later.epochNanoseconds && !raw.offset) {
      add("time_ambiguous", "offset", `This clock time occurs twice. Choose ${earlier.offset} or ${later.offset}.`);
    } else if (raw.offset && raw.offset !== earlier.offset && raw.offset !== later.offset) {
      add("offset_invalid", "offset", "The selected UTC offset does not match this local date and time.");
    } else {
      const resolved = raw.offset === later.offset ? later : earlier;
      point.offset = resolved.offset; point.utc = utcString(resolved.toInstant());
    }
  } catch { add("time_unresolvable", "", "This date and time cannot be resolved in this timezone."); }
  return { ...evaluation(issues, true), point };
}

/** Inclusive all-day dates resolve by calendar midnights, not 24-hour offsets. */
export function resolveAllDay(startDate: unknown, endDate: unknown, timezone: unknown): { startUtc: string; endUtc: string } | null {
  if (!dateStr.safeParse(startDate).success || !dateStr.safeParse(endDate).success || !scheduleTimezone.safeParse(timezone).success || (startDate as string) > (endDate as string)) return null;
  try {
    const first = Temporal.PlainDate.from(startDate as string), last = Temporal.PlainDate.from(endDate as string);
    const begin = first.toZonedDateTime(timezone as string), finish = last.add({ days: 1 }).toZonedDateTime(timezone as string);
    if (!begin.toPlainDate().equals(first) || !last.toZonedDateTime(timezone as string).toPlainDate().equals(last)) return null;
    return { startUtc: utcString(begin.toInstant()), endUtc: utcString(finish.toInstant()) };
  } catch { return null; }
}

/** Pure resolution shared by browser, server and audits. Invalid stored fields
 * are inspected independently, never transformed by create-input defaults. */
export function resolveSchedule(input: unknown, options: { id?: string } = {}): ScheduleResolution {
  const raw = parseScheduleJson(input), data = asObject(raw), issues: ValidationIssue[] = [];
  const add = (code: string, fields: string[], message: string, kind: ValidationIssue["kind"] = "error", blocks: ValidationIssue["blocks"] = ["booking.save", "booking.reserve", "contract.send"]) => {
    issues.push({ code: `booking.${code}`, kind, entity: { type: "booking", ...(options.id ? { id: options.id } : {}) }, fields: fields.length ? fields.map(field => `schedule.${field}`) : ["schedule"], message, blocks });
  };
  const parsed = scheduleV1.safeParse(raw);
  if (!parsed.success) for (const error of parsed.error.issues) add("schedule_invalid", [error.path.map(String).join(".")].filter(Boolean), error.message);
  const zone = scheduleTimezone.safeParse(data.timezone).success ? data.timezone as string : null;

  function point(value: unknown, path: string): ResolvedPoint {
    const result = resolveLocalPoint(value, data.timezone, { entity: { type: "booking", ...(options.id ? { id: options.id } : {}) }, field: `schedule.${path}` });
    // Primitive errors already came from the canonical schema above.
    issues.push(...result.issues.filter(issue => !["booking.point_invalid", "booking.timezone_invalid"].includes(issue.code)));
    return result.point;
  }
  const eventStart = point(data.eventStart, "eventStart"), eventEnd = point(data.eventEnd, "eventEnd");
  function access(value: unknown, anchor: ResolvedPoint, direction: -1 | 1, path: string): ResolvedPoint {
    const parsedChoice = accessChoice.safeParse(value);
    if (!parsedChoice.success) {
      // Preserve independently valid parts of a malformed custom point for chronology.
      return asObject(value).mode === "custom" ? point(asObject(value).point, `${path}.point`) : emptyPoint();
    }
    const choice = parsedChoice.data;
    if (choice.mode === "custom") return point(choice.point, `${path}.point`);
    if (choice.mode === "none" || (choice.mode === "relative" && choice.minutes === 0)) return { ...anchor };
    if (choice.mode === "unknown" || !anchor.utc || !zone) return emptyPoint();
    try {
      const instant = Temporal.Instant.from(anchor.utc).add({ minutes: direction * choice.minutes });
      const local = instant.toZonedDateTimeISO(zone);
      const date = local.toPlainDate().toString();
      if (!dateStr.safeParse(date).success) throw new RangeError("Outside calendar range");
      const time = local.toPlainTime().toString({ smallestUnit: anchor.time?.length === 8 ? "second" : "minute" });
      return { date, time, offset: local.offset, local: `${date}T${time}`, utc: utcString(instant) };
    } catch {
      add("duration_invalid", [`${path}.minutes`], "This duration puts access outside the supported calendar range.");
      return emptyPoint();
    }
  }
  const accessStart = access(data.accessStart, eventStart, -1, "accessStart"), accessEnd = access(data.accessEnd, eventEnd, 1, "accessEnd");
  const points = { accessStart, eventStart, eventEnd, accessEnd };
  const pointFields = ["accessStart", "eventStart", "eventEnd", "accessEnd"] as const;
  for (let i = 0; i < pointFields.length; i++) for (let j = i + 1; j < pointFields.length; j++) {
    const leftName = pointFields[i], rightName = pointFields[j], left = points[leftName], right = points[rightName];
    const compare = left.utc && right.utc ? Temporal.Instant.compare(left.utc, right.utc)
      : left.date && right.date ? left.date.localeCompare(right.date) : null;
    // Any pair spanning the positive event interval must itself be positive,
    // even when one of the intermediate event points is still unknown.
    const equalEvent = i <= 1 && j >= 2 && !!left.utc && !!right.utc && compare === 0;
    if (compare !== null && (compare > 0 || equalEvent)) add(equalEvent ? "event_duration_invalid" : "chronology_invalid", [leftName, rightName], equalEvent ? "These milestones must allow the event to end after it starts." : `${rightName === "accessEnd" ? "Everyone out" : rightName === "eventEnd" ? "Event end" : "Event start"} cannot be before ${leftName === "accessStart" ? "access starts" : leftName === "eventStart" ? "event start" : "event end"}.`);
  }
  if (!eventStart.date) add("event_date_missing", ["eventStart.date"], "Choose the event start date.", "missing", ["booking.save", "booking.reserve", "contract.send"]);
  for (const name of ["eventStart", "eventEnd"] as const) {
    if (!points[name].date) { if (name !== "eventStart") add("event_end_date_missing", [`${name}.date`], "Confirm the event end date.", "missing", ["contract.send"]); }
    if (!points[name].time) add("event_time_missing", [`${name}.time`], "Confirm the event time.", "missing", ["contract.send"]);
  }
  for (const name of ["accessStart", "accessEnd"] as const) if (!points[name].date || !points[name].time) {
    add("access_point_missing", [name], `Confirm ${name === "accessStart" ? "when venue access starts" : "when everyone must be out"}.`, "missing", ["contract.send"]);
  }
  const duration = (start: ResolvedPoint, end: ResolvedPoint) => start.utc && end.utc
    ? Number(Temporal.Instant.from(end.utc).epochNanoseconds - Temporal.Instant.from(start.utc).epochNanoseconds) / 60_000_000_000 : null;
  const accessDuration = (value: unknown, start: ResolvedPoint, end: ResolvedPoint) => {
    const choice = accessChoice.safeParse(value);
    return choice.success && choice.data.mode === "none" ? 0
      : choice.success && choice.data.mode === "relative" ? choice.data.minutes : duration(start, end);
  };
  const durations = { setupMinutes: accessDuration(data.accessStart, accessStart, eventStart), eventMinutes: duration(eventStart, eventEnd), cleanupMinutes: accessDuration(data.accessEnd, eventEnd, accessEnd), accessMinutes: duration(accessStart, accessEnd) };
  let accessStartUtc: string | null = null, accessEndUtc: string | null = null, calendarFrom: string | null = null, calendarThrough: string | null = null;
  let reservationState: ScheduleResolution["reservationState"] = "invalid";
  let legacyBounds: ScheduleResolution["legacyBounds"] = null;
  const parsedReservation = reservation.safeParse(data.reservation);
  if (parsedReservation.success) {
    const occupancy = parsedReservation.data;
    if (occupancy.mode === "timed") {
      if (!accessStart.utc || !accessEnd.utc) {
        reservationState = "incomplete";
        add("access_incomplete", ["accessStart", "accessEnd", "reservation"], "Choose exact access times or an explicit full-day reservation before reserving.", "missing", ["booking.reserve", "contract.send"]);
      } else if (Temporal.Instant.compare(accessStart.utc, accessEnd.utc) >= 0) {
        add("access_duration_invalid", ["accessStart", "accessEnd"], "Venue access must end after it starts.");
      } else {
        reservationState = "timed"; accessStartUtc = accessStart.utc; accessEndUtc = accessEnd.utc;
        calendarFrom = accessStart.date;
        calendarThrough = Temporal.Instant.from(accessEndUtc).subtract({ nanoseconds: 1 }).toZonedDateTimeISO(zone!).toPlainDate().toString();
      }
    } else {
      const legacy = occupancy.mode === "legacy_unresolved";
      if (legacy) {
        legacyBounds = { startDate: occupancy.startDate, endDate: occupancy.endDate, unknownBounds: occupancy.unknownBounds };
        add("legacy_schedule_review", ["reservation"], occupancy.reason, "warning", ["booking.reserve", "contract.send"]);
      }
      reservationState = legacy ? "legacy_unresolved" : "all_day";
      if (legacy && occupancy.unknownBounds) {
        add("legacy_bounds_unknown", ["reservation"], "The old reserved dates are uncertain. Availability remains protected until staff review.", "warning", ["booking.reserve", "contract.send"]);
      } else if (!occupancy.startDate || !occupancy.endDate || occupancy.startDate > occupancy.endDate) {
        add("reservation_dates_invalid", ["reservation.startDate", "reservation.endDate"], "The reservation needs an ordered, complete date range.");
        reservationState = "invalid";
      } else {
        calendarFrom = occupancy.startDate; calendarThrough = occupancy.endDate;
        if (zone) try {
          // Calendar addition, not fixed 24-hour arithmetic. A midnight gap is
          // a real start-of-day transition; an entirely skipped date is invalid.
          const interval = resolveAllDay(occupancy.startDate, occupancy.endDate, zone);
          if (!interval) throw new RangeError("Skipped date");
          accessStartUtc = interval.startUtc; accessEndUtc = interval.endUtc;
          for (const name of pointFields) {
            const known = points[name];
            // Last-day midnight release belongs to the preceding occupied day.
            const endPoint = name === "eventEnd" || name === "accessEnd";
            const outside = known.utc ? Temporal.Instant.compare(known.utc, accessStartUtc) < 0 || Temporal.Instant.compare(known.utc, accessEndUtc) >= (endPoint ? 1 : 0)
              : known.date ? known.date < occupancy.startDate || known.date > occupancy.endDate : false;
            if (outside) add("reservation_excludes_milestone", ["reservation", name], "The reserved dates must cover every known event and access milestone.");
          }
        } catch {
          accessStartUtc = null; accessEndUtc = null; reservationState = "invalid";
          add("reservation_dates_unresolvable", ["reservation"], "These reserved dates cannot be resolved in the booking timezone.");
        }
      }
    }
  }
  return { ...evaluation(issues, true), schedule: parsed.success ? parsed.data : null, points, durations, reservationState, legacyBounds,
    projections: { eventDate: eventStart.date, eventEndDate: eventEnd.date,
      // Legacy NOT NULL compatibility display only: never used for occupancy.
      endDate: accessEnd.date ?? calendarThrough ?? eventEnd.date ?? eventStart.date,
      setupStartsAt: accessStart.local, startsAt: eventStart.local, endsAt: eventEnd.local, vacateAt: accessEnd.local,
      accessStartUtc, accessEndUtc, calendarFrom, calendarThrough } };
}

export const BLOCKING_BOOKING_STATUSES = ["contract_sent", "signed", "booked", "completed"] as const;
export interface BlockingBooking { status?: unknown; holdStatus?: unknown; holdExpiresAt?: unknown }
/** Invalid injected now cannot release a confirmed hold. Cancellation wins. */
export function bookingBlocks(record: BlockingBooking | Record<string, unknown>, now: string | Date): boolean {
  if (record.status === "cancelled") return false;
  if (BLOCKING_BOOKING_STATUSES.some(status => status === record.status)) return true;
  if (record.holdStatus !== "confirmed") return false;
  try {
    if (!instantDateTime.safeParse(record.holdExpiresAt).success) return true;
    return Temporal.Instant.compare(record.holdExpiresAt as string, now instanceof Date ? now.toISOString() : now) > 0;
  } catch { return true; }
}
export function intervalsOverlap(start: string, end: string, otherStart: string, otherEnd: string): boolean {
  try { return Temporal.Instant.compare(start, otherEnd) < 0 && Temporal.Instant.compare(otherStart, end) < 0; } catch { return false; }
}

export interface LegacyScheduleRecord {
  eventDate?: unknown; endDate?: unknown; setupStartsAt?: unknown; startsAt?: unknown; endsAt?: unknown; vacateAt?: unknown;
}
/** Migration/read-only preview: preserve old inclusive occupied DAYS even when
 * exact times exist. Only the separately guarded reviewed narrowing may shrink. */
export function legacySchedule(record: LegacyScheduleRecord | Record<string, unknown>, timezone: string): ScheduleV1 {
  record = asObject(record);
  const fields = ["eventDate", "endDate", "setupStartsAt", "startsAt", "endsAt", "vacateAt"] as const;
  const raw: Record<string, string | null> = {};
  for (const field of fields) {
    const value = record[field];
    raw[field] = value === null || value === undefined ? null : typeof value === "string" ? value : String(value);
  }
  const fromTimestamp = (value: unknown): LocalPoint => {
    if (typeof value !== "string") return { date: null, time: null };
    const [date, time, extra] = value.split("T");
    return { date: validDate(date), time: extra === undefined ? validTime(time) : null };
  };
  const start = fromTimestamp(record.startsAt), end = fromTimestamp(record.endsAt);
  if (!start.date) start.date = validDate(record.eventDate);
  const custom = (value: unknown): AccessChoice => value === undefined || value === null ? { mode: "unknown" } : { mode: "custom", point: fromTimestamp(value) };
  // Include all trustworthy evidence, not merely the old coalesce endpoints:
  // contradictory dates can expand coverage, but can never silently shrink it.
  const dates = fields.map(field => validDate(typeof record[field] === "string" ? (record[field] as string).slice(0, 10) : null)).filter((date): date is string => date !== null).sort();
  const oldStart = record.setupStartsAt === null || record.setupStartsAt === undefined ? validDate(record.eventDate) : validDate(typeof record.setupStartsAt === "string" ? record.setupStartsAt.slice(0, 10) : null);
  const oldEnd = record.vacateAt === null || record.vacateAt === undefined ? validDate(record.endDate) : validDate(typeof record.vacateAt === "string" ? record.vacateAt.slice(0, 10) : null);
  return { version: 1, timezone, eventStart: start, eventEnd: end, accessStart: custom(record.setupStartsAt), accessEnd: custom(record.vacateAt),
    reservation: { mode: "legacy_unresolved", startDate: dates[0] ?? null, endDate: dates.at(-1) ?? null, unknownBounds: !oldStart || !oldEnd,
      reason: "Preserved legacy inclusive reserved dates; review source evidence before narrowing venue access.", raw } };
}
